import {
  ClearwaterTransaction,
  ExternalDataSourceType,
} from '@helios/common-api'
import { ExternalTransaction } from '../external-transactions.service'

type FieldDifferencesMapType = Map<
  keyof ClearwaterTransaction,
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ) => FieldDifferenceResults[]
>

export type FieldDifferenceResults = {
  hasDifference: boolean
  amount: unknown
  dataSourceType?: string
}

export const FieldDifferenceMap: FieldDifferencesMapType = new Map()

FieldDifferenceMap.set(
  'amount',
  (cwTx: ClearwaterTransaction): FieldDifferenceResults[] => {
    return calculatedFieldHasDifference(cwTx)
  },
)

FieldDifferenceMap.set(
  'commission',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return doubleFieldHasDifference(cwTx, extTxs, 'commission', 'commission')
  },
)

FieldDifferenceMap.set(
  'units',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return doubleFieldHasDifference(cwTx, extTxs, 'units', 'units')
  },
)

FieldDifferenceMap.set(
  'price',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return doubleFieldHasDifference(cwTx, extTxs, 'price', 'price')
  },
)

FieldDifferenceMap.set(
  'accrued',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return doubleFieldHasDifference(cwTx, extTxs, 'accrued', 'accrued')
  },
)

FieldDifferenceMap.set(
  'entryDate',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return stringFieldHasSomeDifference(cwTx, extTxs, 'entryDate', 'tradeDate')
  },
)

FieldDifferenceMap.set(
  'settleDate',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return stringFieldHasSomeDifference(
      cwTx,
      extTxs,
      'settleDate',
      'settleDate',
    )
  },
)

FieldDifferenceMap.set(
  'postDate',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return stringFieldHasSomeDifference(cwTx, extTxs, 'postDate', 'postDate')
  },
)

FieldDifferenceMap.set(
  'secIdentifier',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return stringFieldHasSomeDifference(
      cwTx,
      extTxs,
      'secIdentifier',
      'secIdentifier',
    )
  },
)

FieldDifferenceMap.set(
  'tranTypeCode',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return stringFieldHasSomeDifference(
      cwTx,
      extTxs,
      'tranTypeCode',
      'tranTypeCode',
    )
  },
)

FieldDifferenceMap.set(
  'currencyCode',
  (
    cwTx: ClearwaterTransaction,
    extTxs: ExternalTransaction[],
  ): FieldDifferenceResults[] => {
    return stringFieldHasSomeDifference(
      cwTx,
      extTxs,
      'currencyCode',
      'currencyCode',
    )
  },
)

/**
 *
 */
export function doubleFieldHasDifference(
  cwTx: ClearwaterTransaction,
  extTxs: ExternalTransaction[],
  cwTxField: keyof ClearwaterTransaction,
  extTxField: keyof ExternalTransaction,
): FieldDifferenceResults[] {
  const cwTxValue = cwTx[cwTxField] as number
  if (cwTxValue == undefined)
    return [
      {
        hasDifference: false,
        amount: undefined,
        dataSourceType: undefined,
      },
    ]

  const extAmtSumBySource = sumBySourceAndField(extTxs, extTxField)
  const extAmtSumBySourceArray = []

  for (const amountSum of extAmtSumBySource) {
    if (amountSum[1] !== cwTxValue) {
      extAmtSumBySourceArray.push({
        hasDifference: true,
        amount: amountSum[1] - cwTxValue,
        dataSourceType: amountSum[0],
      })
    }
  }
  if (extAmtSumBySourceArray.length !== 0) {
    return extAmtSumBySourceArray
  }
  return [
    { hasDifference: false, amount: undefined, dataSourceType: undefined },
  ]
}

/**
 *
 */
export function calculatedFieldHasDifference(
  cwTx: ClearwaterTransaction,
): FieldDifferenceResults[] {
  const fieldDiffArray: FieldDifferenceResults[] = []
  const formattedCustodyAmountDifference = Number(
    cwTx.custodyAmountDifference?.toFixed(4),
  )
  const formattedTradingAmountDifference = Number(
    cwTx.tradingAmountDifference?.toFixed(4),
  )
  if (!formattedCustodyAmountDifference && !formattedTradingAmountDifference) {
    return [
      {
        hasDifference: false,
        amount: undefined,
        dataSourceType: undefined,
      },
    ]
  }

  if (formattedCustodyAmountDifference) {
    fieldDiffArray.push({
      hasDifference: true,
      amount: cwTx.custodyAmountDifference,
      dataSourceType: ExternalDataSourceType.CUSTODY,
    })
  }

  if (formattedTradingAmountDifference) {
    fieldDiffArray.push({
      hasDifference: true,
      amount: cwTx.tradingAmountDifference,
      dataSourceType: ExternalDataSourceType.TRADING,
    })
  }
  return fieldDiffArray
}

/**
 *
 */
export function sumBySourceAndField(
  extTxs: ExternalTransaction[],
  field: keyof ExternalTransaction,
): Map<string, number> {
  const sumBySource = new Map<string, number>()
  for (const tran of extTxs) {
    const fieldValue = tran[field] as number
    if (fieldValue == undefined || tran.dataSourceType == undefined) continue
    const prevSum = sumBySource.get(tran.dataSourceType) ?? 0
    sumBySource.set(tran.dataSourceType, prevSum + fieldValue)
  }
  return sumBySource
}

/**
 *
 */
export function stringFieldHasSomeDifference(
  cwTx: ClearwaterTransaction,
  extTxs: ExternalTransaction[],
  cwTxField: keyof ClearwaterTransaction,
  extTxField: keyof ExternalTransaction,
): FieldDifferenceResults[] {
  const cwTxValue = cwTx[cwTxField] as string
  if (cwTxValue == undefined)
    return [
      {
        hasDifference: false,
        amount: undefined,
        dataSourceType: undefined,
      },
    ]

  const valuesBySource = gatherBySourceAndField<string>(extTxs, extTxField)
  const valuesBySourceArray = []

  for (const values of valuesBySource) {
    if (!values[1].some(value => cwTxValue === value))
      valuesBySourceArray.push({
        hasDifference: true,
        amount: values[1].join(','),
        dataSourceType: values[0],
      })
  }
  if (valuesBySourceArray.length !== 0) {
    return valuesBySourceArray
  }
  return [
    { hasDifference: false, amount: undefined, dataSourceType: undefined },
  ]
}

/**
 *
 */
export function gatherBySourceAndField<T>(
  extTxs: ExternalTransaction[],
  field: keyof ExternalTransaction,
): Map<string, T[]> {
  const valuesBySource = new Map<string, T[]>()
  for (const tran of extTxs) {
    const fieldValue = tran[field] as unknown as T
    if (fieldValue == undefined || tran.dataSourceType == undefined) continue
    let values: T[] | undefined = valuesBySource.get(tran.dataSourceType)

    if (values == null) {
      values = []
      valuesBySource.set(tran.dataSourceType, values)
    }

    values.push(fieldValue)
  }
  return valuesBySource
}
